// The Operator API: the same tools the voice copilot uses, exposed on
// window.blastRadius for scripts and browser-driving agents. Every call is
// recorded with source "api", so the run report can see delegation.

import { LIVE_TOOLS } from '../voice/tools';
import type { WorldEvent } from '../core/types';
import type { Session } from './session';

export const OPERATOR_MANUAL = `blastRadius.help()                 this text
blastRadius.state()                what the copilot sees: budget, labels, positions, problems
blastRadius.do("build two benches near forge 1")
                                   plain-language commands, same as the command bar
blastRadius.tool("connect", { from: "Forge 1", to: "Bench 1" })
                                   any copilot tool with JSON arguments
blastRadius.tools()                tool names and their arguments
blastRadius.speed(4)               0 pauses; 1, 2 or 4
blastRadius.toScreen(x, y)         screen pixels for a tile, for agents that click
blastRadius.on(fn)                 fn(event) for every run-log event; returns stop()
blastRadius.every(ms, fn)          fn(state, api) on a timer; returns stop()

Example: roll back automatically whenever something is corrupted
  blastRadius.every(1000, (s, api) => {
    if (s.problems.some(p => p.includes("corrupted"))) api.tool("rollback", {})
  })

Everything here is recorded as yours, tagged "api".`;

type Listener = (e: WorldEvent) => void;

export interface OperatorApi {
  help(): string;
  state(): unknown;
  do(text: string): { ok: boolean; message: string };
  tool(name: string, args?: Record<string, unknown>): { ok: boolean; message: string; data?: unknown };
  tools(): { name: string; description: string; parameters: Record<string, unknown> }[];
  speed(n: number): { ok: boolean; message: string };
  on(fn: Listener): () => boolean;
  every(ms: number, fn: (state: unknown, api: OperatorApi) => void): () => void;
  stopAll(): void;
  toScreen(x: number, y: number): { x: number; y: number };
  view(): { cx: number; cy: number; zoom: number; width: number; height: number };
}

export function installApi(getSession: () => Session | null) {
  const timers = new Set<number>();
  const need = () => {
    const s = getSession();
    if (!s) throw new Error('No level running. Start one first.');
    if (s.level.flags.unassisted) throw new Error('This level is unassisted: no scripts and no copilot.');
    return s;
  };
  const api: OperatorApi = {
    help() {
      console.log(OPERATOR_MANUAL);
      return 'See above.';
    },
    state() {
      return need().runTool('get_state', {}, 'api').data;
    },
    do(text: string) {
      return need().exec(text, 'api');
    },
    tool(name: string, args: Record<string, unknown> = {}) {
      return need().runTool(name, args, 'api');
    },
    tools() {
      return LIVE_TOOLS.map((t) => ({ name: t.name, description: t.description, parameters: t.parameters }));
    },
    speed(n: number) {
      return need().runTool('set_speed', { speed: n }, 'api');
    },
    on(fn: Listener) {
      const s = need();
      s.listeners.add(fn);
      return () => s.listeners.delete(fn);
    },
    every(ms: number, fn: (state: unknown, a: OperatorApi) => void) {
      const id = window.setInterval(() => {
        const s = getSession();
        if (!s || s.world.outcome !== 'playing') return;
        try {
          fn(s.runTool('get_state', {}, 'api').data, api);
        } catch (err) {
          console.warn('blastRadius.every callback failed', err);
        }
      }, Math.max(100, ms));
      timers.add(id);
      return () => {
        window.clearInterval(id);
        timers.delete(id);
      };
    },
    stopAll() {
      for (const id of timers) window.clearInterval(id);
      timers.clear();
    },
    toScreen(x: number, y: number) {
      const [sx, sy] = need().renderer.cam.toScreen(x, y);
      return { x: sx, y: sy };
    },
    view() {
      const c = need().renderer.cam;
      return { cx: c.cx, cy: c.cy, zoom: c.zoom, width: c.vw, height: c.vh };
    },
  };
  (window as unknown as { blastRadius: OperatorApi }).blastRadius = api;
  return api;
}
